import type { Modality } from "@/shared/schemas/modalities";
import { getModalityName } from "@/shared/schemas/modalities";

/**
 * Configuración del chart para shadcn (configuración estática por compatibilidad)
 * @constant {Object} chartConfig - Configuración del chart
 */
export const chartConfig = {
  consulta: {
    label: "Consulta",
    color: "var(--chart-1)",
  },
  emergencia: {
    label: "Emergencia",
    color: "var(--chart-2)",
  },
  cirugia: {
    label: "Cirugía",
    color: "var(--chart-3)",
  },
  laboratorio: {
    label: "Laboratorio",
    color: "var(--chart-4)",
  },
  radiologia: {
    label: "Radiología",
    color: "var(--chart-5)",
  },
};

/**
 * Genera configuración de chart dinámica basada en modalidades
 * @param modalities - Array de modalidades
 * @param language - Idioma para los nombres ('en' | 'es')
 * @returns Configuración del chart para las modalidades
 */
export function generateModalitiesChartConfig(
  modalities: Modality[],
  language: "en" | "es" = "es"
): Record<string, { label: string; color: string }> {
  return modalities.reduce(
    (config, modality, index) => {
      const colorIndex = index % MODALITIES_COLORS.length;
      config[modality.abbr] = {
        label: getModalityName(modality, language),
        color: MODALITIES_COLORS[colorIndex],
      };
      return config;
    },
    {} as Record<string, { label: string; color: string }>
  );
}

/**
 * Paleta de colores expandida para modalidades médicas
 * Diseñada para soportar hasta 40 modalidades diferentes
 * @constant {string[]} MODALITIES_COLORS - Colores para charts de modalidades
 */
export const MODALITIES_COLORS = [
  // Colores primarios del sistema de diseño
  "var(--chart-1)", // Azul
  "var(--chart-2)", // Verde
  "var(--chart-3)", // Amarillo
  "var(--chart-4)", // Rojo
  "var(--chart-5)", // Púrpura

  // Colores adicionales para modalidades médicas
  "hsl(200, 70%, 50%)", // Azul claro
  "hsl(120, 60%, 45%)", // Verde esmeralda
  "hsl(30, 80%, 55%)", // Naranja
  "hsl(280, 65%, 60%)", // Violeta
  "hsl(0, 70%, 60%)", // Rojo coral
  "hsl(180, 70%, 45%)", // Cian
  "hsl(60, 80%, 50%)", // Amarillo dorado
  "hsl(320, 70%, 55%)", // Rosa magenta
  "hsl(40, 90%, 60%)", // Amarillo mostaza
  "hsl(220, 60%, 50%)", // Azul marino
  "hsl(140, 50%, 45%)", // Verde oliva
  "hsl(300, 60%, 55%)", // Púrpura claro
  "hsl(15, 85%, 60%)", // Rojo terracota
  "hsl(240, 70%, 60%)", // Azul índigo
  "hsl(80, 70%, 50%)", // Verde lima
  "hsl(260, 65%, 55%)", // Púrpura profundo
  "hsl(350, 75%, 55%)", // Rojo carmesí
  "hsl(160, 60%, 45%)", // Verde azulado
  "hsl(45, 85%, 60%)", // Amarillo ámbar
  "hsl(270, 60%, 50%)", // Púrpura medio
  "hsl(10, 80%, 55%)", // Rojo óxido
  "hsl(190, 70%, 50%)", // Azul turquesa
  "hsl(100, 60%, 45%)", // Verde bosque
  "hsl(330, 70%, 60%)", // Rosa fucsia
  "hsl(50, 90%, 55%)", // Amarillo crema
  "hsl(210, 65%, 45%)", // Azul acero
  "hsl(130, 70%, 50%)", // Verde menta
  "hsl(25, 85%, 60%)", // Naranja dorado
  "hsl(290, 60%, 55%)", // Púrpura lavanda
  "hsl(5, 75%, 50%)", // Rojo vino
  "hsl(170, 60%, 45%)", // Verde agua
  "hsl(35, 80%, 55%)", // Naranja melocotón
  "hsl(250, 70%, 60%)", // Azul púrpura
  "hsl(90, 70%, 50%)", // Verde chartreuse
  "hsl(310, 65%, 55%)", // Rosa púrpura
  "hsl(110, 60%, 45%)", // Verde oliva claro
  "hsl(230, 65%, 55%)", // Azul púrpura oscuro
  "hsl(10, 85%, 60%)", // Rojo terracota claro
  "hsl(260, 70%, 60%)", // Púrpura violeta claro
  "hsl(60, 85%, 55%)", // Amarillo lima claro
  "hsl(280, 75%, 60%)", // Azul marino oscuro
  "hsl(340, 70%, 55%)", // Rosa magenta oscuro
  "hsl(45, 95%, 60%)", // Amarillo mostaza oscuro
  "hsl(215, 65%, 50%)", // Azul acero oscuro
  "hsl(140, 75%, 55%)", // Verde menta oscuro
  "hsl(270, 80%, 60%)", // Púrpura lavanda oscuro
  "hsl(5, 85%, 50%)", // Rojo vino oscuro
];

/**
 * Colores legacy para compatibilidad con código existente
 * @constant {string[]} COLORS - Colores originales del chart
 * @deprecated Usar MODALITIES_COLORS en su lugar
 */
export const COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];
